import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useApi from "@/hooks/useApi";
import MoshkyAnalyticsPanel from "@/components/MoshkyAnalyticsPanel";

interface CsatSummary {
  totalResponses: number;
  averageRating: number | null;
  satisfactionRate: number | null;
  breakdown: { rating: number; count: number }[];
  recentComments: { rating: number; comment: string; channel: string; respondedAt: string }[];
  days: number;
}

interface TeamStat {
  teamId: number;
  totalResponses: number;
  avgRating: number;
  satisfactionRate: number;
}

interface Team {
  id: number;
  name: string;
}

const PERIODS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

function StarDisplay({ rating }: { rating: number }) {
  return (
    <span className="text-amber-400">
      {[1,2,3,4,5].map(s => (
        <span key={s} className={s <= rating ? "opacity-100" : "opacity-20"}>★</span>
      ))}
    </span>
  );
}

function scoreColor(rate: number | null) {
  if (rate == null) return { text: "text-slate-400", bar: "bg-slate-300" };
  if (rate >= 80) return { text: "text-violet-600", bar: "bg-violet-500" };
  if (rate >= 60) return { text: "text-amber-600", bar: "bg-amber-400" };
  return { text: "text-rose-600", bar: "bg-rose-400" };
}

function SatisfactionGauge({ rate }: { rate: number | null }) {
  const pct = rate ?? 0;
  const colors = scoreColor(rate);
  return (
    <div className="flex flex-col items-center gap-1">
      <span className={`text-3xl font-bold tabular-nums ${colors.text}`}>
        {rate !== null ? `${rate}%` : "—"}
      </span>
      <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
        <div className={`h-1.5 rounded-full transition-all duration-500 ${colors.bar}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function TeamRankWidget({ teamStats, teams }: { teamStats: TeamStat[]; teams: Team[] }) {
  if (teamStats.length === 0) return null;
  const nameMap = new Map(teams.map(t => [t.id, t.name]));
  const medals = ["🥇", "🥈", "🥉", "4th", "5th"];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <h3 className="text-sm font-semibold text-slate-700 mb-3">Top Teams by Satisfaction</h3>
      <div className="space-y-3">
        {teamStats.map((t, i) => {
          const colors = scoreColor(t.satisfactionRate);
          return (
            <div key={t.teamId} className="flex items-center gap-3">
              <span className="text-base w-8 text-center shrink-0">{medals[i]}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-sm font-medium text-slate-700 truncate">
                    {nameMap.get(t.teamId) ?? `Team ${t.teamId}`}
                  </span>
                  <span className={`text-xs font-bold ml-2 shrink-0 ${colors.text}`}>
                    {t.satisfactionRate.toFixed(0)}%
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                    <div className={`h-1.5 rounded-full transition-all duration-500 ${colors.bar}`} style={{ width: `${t.satisfactionRate}%` }} />
                  </div>
                  <span className="text-[10px] text-slate-400 shrink-0 w-14 text-right">{t.totalResponses} resp.</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CsatReport() {
  const api = useApi();
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<CsatSummary | null>(null);
  const [teamStats, setTeamStats] = useState<TeamStat[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.admin.get(`Csat/Summary?days=${days}`),
      api.admin.get(`Csat/Summary/ByTeam?days=${days}`),
      api.messaging.get("Leaderboard/teams"),
    ]).then(async ([sumRes, teamRes, teamNamesRes]) => {
      if (sumRes.ok) setSummary(await sumRes.json());
      if (teamRes.ok) setTeamStats(await teamRes.json());
      if (teamNamesRes.ok) setTeams(await teamNamesRes.json());
    }).catch(() => {}).finally(() => setLoading(false));
  }, [api.admin, api.messaging, days]);

  return (
    <div className="p-6 max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">CSAT Survey Results</h2>
          <p className="text-sm text-slate-500 mt-0.5">Customer satisfaction scores from post-chat surveys.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            {PERIODS.map(p => (
              <button
                key={p.days}
                onClick={() => setDays(p.days)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${days === p.days ? "bg-white shadow-sm text-slate-800" : "text-slate-500 hover:text-slate-700"}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {summary && <MoshkyAnalyticsPanel reportName="CSAT Results" reportData={{ summary, period: `${days} days` }} />}
          <Link
            to="/settings?tab=csat"
            className="text-xs text-primary hover:text-cornflower-blue-800 border border-cornflower-blue-200 rounded-lg px-3 py-1.5 transition-colors"
          >
            Configure surveys →
          </Link>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : !summary || summary.totalResponses === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="text-4xl mb-3">📊</div>
          <p className="text-slate-600 font-medium">No responses yet</p>
          <p className="text-sm text-slate-400 mt-1">Enable CSAT surveys in Settings to start collecting responses.</p>
          <Link to="/settings?tab=csat" className="mt-4 text-sm text-primary hover:underline">Configure CSAT surveys →</Link>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-slate-900">{summary.totalResponses}</p>
              <p className="text-xs text-slate-500 mt-1 font-medium uppercase tracking-wide">Total Responses</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
              <SatisfactionGauge rate={summary.satisfactionRate} />
              <p className="text-xs text-slate-500 mt-1.5 font-medium uppercase tracking-wide">Satisfaction Rate</p>
              <p className="text-xs text-slate-400 mt-0.5">(ratings 4–5 out of 5)</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-slate-900">
                {summary.averageRating !== null ? summary.averageRating.toFixed(1) : "—"}
              </p>
              <p className="text-xs text-slate-500 mt-1 font-medium uppercase tracking-wide">Avg Rating</p>
              <div className="mt-1 flex justify-center">
                {summary.averageRating !== null && <StarDisplay rating={Math.round(summary.averageRating)} />}
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h3 className="text-sm font-medium text-slate-700 mb-3">Rating breakdown</h3>
            <div className="space-y-2">
              {[5,4,3,2,1].map(r => {
                const count = summary.breakdown.find(b => b.rating === r)?.count ?? 0;
                const pct = summary.totalResponses > 0 ? (count / summary.totalResponses * 100) : 0;
                const barColor = r >= 4 ? "bg-violet-500" : r === 3 ? "bg-amber-400" : "bg-rose-400";
                return (
                  <div key={r} className="flex items-center gap-3">
                    <span className="text-xs w-4 text-slate-500 text-right">{r}★</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-2.5">
                      <div className={`h-2.5 rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs text-slate-500 w-16 text-right">{count} ({pct.toFixed(0)}%)</span>
                  </div>
                );
              })}
            </div>
          </div>

          <TeamRankWidget teamStats={teamStats} teams={teams} />

          {Array.isArray(summary.recentComments) && summary.recentComments.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <h3 className="text-sm font-medium text-slate-700 mb-3">Recent comments</h3>
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {summary.recentComments.map((c, i) => (
                  <div key={i} className="border border-slate-100 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1.5">
                      <StarDisplay rating={c.rating} />
                      {c.channel && (
                        <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-medium uppercase">{c.channel}</span>
                      )}
                      <span className="text-xs text-slate-400 ml-auto">{new Date(c.respondedAt).toLocaleDateString()}</span>
                    </div>
                    <p className="text-sm text-slate-700 italic">"{c.comment}"</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
